'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStore } from '@/app/providers/store';
import type { useClawFolder } from '../../../_hook/use-claw-folder';
import { CLAW_UPLOAD_ACCEPT, DEFAULT_MEDIA_ROOT, normalizePath, reportClawCreationOperation } from '../../../_utils';
import type { ClawFileItem, MediaViewMode } from '../../../types';
import { CreateFolderDialog } from '../../File/components/CreateFolderDialog';
import { FilePreviewPanel } from '../../File/components/FilePreviewPanel';
import { FileTable } from '../../File/components/FileTable';
import { useClawFileOperations } from '../../File/hooks/use-claw-file-operations';
import { useFileExplorer } from '../../File/hooks/use-file-explorer';
import { isFolder } from '../../File/utils';
import { MediaBrowserTitleHeader } from './MediaBrowserHeader';

type ClawFolderApi = ReturnType<typeof useClawFolder>;

interface MediaBrowserTreeProps {
  activeClawId?: string;
  clawFolderApi: ClawFolderApi;
  viewMode: MediaViewMode;
  onViewModeChange: (mode: MediaViewMode) => void;
  previewTargetFilePath?: string | null;
  onPreviewHandled?: () => void;
  /** 外层正在切 session/恢复目录的窗口期，给面板覆盖一层 loading 避免显示残留 */
  syncing?: boolean;
  /** 外层拖拽 drop 暂存的待上传文件，消费后调用 onPendingDropFilesHandled 清空 */
  pendingDropFiles?: File[] | null;
  onPendingDropFilesHandled?: () => void;
}

export const MediaBrowserTree = ({
  activeClawId,
  clawFolderApi,
  viewMode,
  onViewModeChange,
  previewTargetFilePath,
  onPreviewHandled,
  syncing = false,
  pendingDropFiles,
  onPendingDropFilesHandled,
}: MediaBrowserTreeProps) => {
  const t = useTranslations();
  const { clawStatus, refreshKey, persistedFolderPath, setFolderPath } = useStore((state) => ({
    clawStatus: state.clawStatus,
    refreshKey: state.clawFileListRefreshKey,
    persistedFolderPath: state.clawFolderPath,
    setFolderPath: state.setClawFolderPath,
  }));

  const { listFolder, cachePreviewUrl, creatingFolder, uploadingFile, deletingPath } = clawFolderApi;

  const {
    expandedFolderMap,
    loadingFolderMap,
    activeSelection,
    initialLoading,
    previewFilePath,
    activeFile,
    currentFolderPath,
    displayRows,
    refreshFolder,
    refreshFolders,
    handleToggleFolder,
    handleSelectFolder,
    handleSelectFile,
    closePreview,
    openPreviewByPath,
    getActionTargetFolderPath,
    handleDeleteSuccess,
  } = useFileExplorer({
    activeClawId,
    refreshKey,
    listFolder,
    rootPath: DEFAULT_MEDIA_ROOT,
    initialPath: persistedFolderPath,
  });

  useEffect(() => {
    if (initialLoading) return;
    if (!currentFolderPath) return;
    if (currentFolderPath === persistedFolderPath) return;
    setFolderPath(currentFolderPath);
  }, [currentFolderPath, initialLoading, persistedFolderPath, setFolderPath]);

  // 树视图首次 init 完成后才能定位预览目标，async 流程需用 ref 防止重复处理同一目标。
  const handledPreviewTargetRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeClawId) return;
    if (initialLoading) return;
    const normalizedTarget = normalizePath(previewTargetFilePath ?? '');
    if (!normalizedTarget) {
      handledPreviewTargetRef.current = null;
      return;
    }
    if (handledPreviewTargetRef.current === normalizedTarget) return;
    handledPreviewTargetRef.current = normalizedTarget;

    void (async () => {
      await openPreviewByPath(normalizedTarget);
      onPreviewHandled?.();
    })();
  }, [activeClawId, initialLoading, previewTargetFilePath, openPreviewByPath, onPreviewHandled]);

  const getDefaultTargetFolderPath = useCallback((): string => {
    if (activeSelection) return getActionTargetFolderPath();
    return currentFolderPath || DEFAULT_MEDIA_ROOT;
  }, [activeSelection, currentFolderPath, getActionTargetFolderPath]);

  const refreshStrategy = useMemo(
    () => ({
      afterCreateFolder: async (targetFolderPath: string) => {
        await refreshFolder(targetFolderPath);
      },
      afterUploadFiles: async (targetFolderPath: string) => {
        await refreshFolder(targetFolderPath);
      },
      afterDelete: async (item: ClawFileItem) => {
        await refreshFolders(handleDeleteSuccess(item));
      },
    }),
    [handleDeleteSuccess, refreshFolder, refreshFolders],
  );

  const fileOps = useClawFileOperations({
    activeClawId,
    clawFolderApi,
    refreshStrategy,
    getDefaultTargetFolderPath,
  });

  const { handleDownload: opsHandleDownload, deleteWithConfirm } = fileOps;

  // 外层拖拽 drop 的文件上传到当前所在/选中目录，上传完成后通知外层清空暂存。
  const { handleDropFiles } = fileOps;
  useEffect(() => {
    if (!pendingDropFiles?.length) return;
    void handleDropFiles(pendingDropFiles).finally(() => onPendingDropFilesHandled?.());
  }, [pendingDropFiles, handleDropFiles, onPendingDropFilesHandled]);

  const handleDownload = useCallback(
    (item: ClawFileItem) => {
      reportClawCreationOperation('download', item.path);
      opsHandleDownload(item);
    },
    [opsHandleDownload],
  );

  const handleDelete = useCallback(
    (item: ClawFileItem) => {
      reportClawCreationOperation('delete', item.path);
      deleteWithConfirm(item);
    },
    [deleteWithConfirm],
  );

  const handleRefreshItem = useCallback(
    async (item: ClawFileItem): Promise<void> => {
      const itemPath = normalizePath(item.path);
      if (isFolder(item)) {
        await refreshFolder(itemPath);
      } else {
        const parentPath = itemPath.split('/').slice(0, -1).join('/');
        await refreshFolder(parentPath);
      }
    },
    [refreshFolder],
  );

  const handleRefreshRoot = useCallback(async (): Promise<void> => {
    await refreshFolder(DEFAULT_MEDIA_ROOT);
  }, [refreshFolder]);

  // 切目录期间 useFileExplorer 串行展开各级，currentFolderPath 还停在旧路径；
  // 通过 persistedFolderPath !== currentFolderPath + loadingFolderMap 在跑来识别"恢复中"。
  const isResolvingPersistedPath =
    !!persistedFolderPath && persistedFolderPath !== currentFolderPath && Object.values(loadingFolderMap).some(Boolean);
  const showInitialLoading = initialLoading || syncing || isResolvingPersistedPath;
  const isEmpty = !showInitialLoading && displayRows.length === 0;

  return (
    <div className="relative flex h-full min-h-0 flex-col border-system-white08 border-l bg-[rgba(255,255,255,0.02)]">
      <div className="border-system-white08 border-b px-4 py-4">
        <MediaBrowserTitleHeader
          title="/media"
          uploadingFile={uploadingFile}
          loading={showInitialLoading}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          onOpenCreateFolder={() => fileOps.createFolderDialog.openFor()}
          onUploadClick={() => fileOps.triggerUpload()}
          onRefresh={handleRefreshRoot}
        />
      </div>

      <input
        ref={fileOps.fileInputRef}
        type="file"
        multiple
        accept={CLAW_UPLOAD_ACCEPT}
        className="hidden"
        onChange={fileOps.handleUploadInputChange}
      />

      <CreateFolderDialog
        open={fileOps.createFolderDialog.open}
        folderName={fileOps.createFolderDialog.folderName}
        creatingFolder={creatingFolder}
        onOpenChange={fileOps.createFolderDialog.setOpen}
        onFolderNameChange={fileOps.createFolderDialog.setFolderName}
        onCreate={fileOps.createFolderDialog.handleCreateFolder}
      />

      <div className="min-h-0 flex-1">
        {showInitialLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-system-white40">
            {t('claw.assets.loading')}
          </div>
        ) : isEmpty ? (
          <div className="flex h-full items-center justify-center text-sm text-system-white40">
            {t('claw.file.empty')}
          </div>
        ) : (
          <FileTable
            clawStatus={clawStatus}
            className="p-0 py-2"
            gridTemplateColumns="minmax(0,1fr) 56px 72px 40px"
            initialLoading={false}
            displayRows={displayRows}
            activeSelection={activeSelection}
            expandedFolderMap={expandedFolderMap}
            loadingFolderMap={loadingFolderMap}
            deletingPath={deletingPath}
            creatingFolder={creatingFolder}
            uploadingFile={uploadingFile}
            onToggleFolder={handleToggleFolder}
            onSelectFolder={handleSelectFolder}
            onSelectFile={handleSelectFile}
            onUpload={fileOps.triggerUpload}
            onCreateFolder={fileOps.createFolderDialog.openFor}
            onDownload={handleDownload}
            onCopyPath={fileOps.handleCopyItemPath}
            onDelete={handleDelete}
            onRefresh={handleRefreshItem}
          />
        )}
      </div>

      {previewFilePath ? (
        <div className="absolute inset-0 z-20 bg-[rgba(16,20,26,0.96)] light:bg-[rgba(255,255,255,0.96)]">
          <FilePreviewPanel
            activeFile={activeFile}
            activeFilePath={previewFilePath}
            clawId={activeClawId}
            onClose={closePreview}
            onResolvedPreviewUrl={cachePreviewUrl}
          />
        </div>
      ) : null}
    </div>
  );
};
